// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.

// frontend/src/components/mission-execution/tabs/HistoryExecutionTab.jsx

import React, { useMemo } from "react";
import ExecutionSection from "../shared/ExecutionSection";
import ExecutionTimeline from "../shared/ExecutionTimeline";

function clean(value) {
  const text = String(value ?? "").trim();
  return text || "";
}

function eventSourceKey(event) {
  const haystack = `${clean(event?.source)} ${clean(event?.metadata?.source)} ${clean(event?.event_source)} ${clean(event?.event_type)} ${clean(event?.type)} ${clean(event?.category)} ${clean(event?.title)} ${clean(event?.message)}`.toLowerCase();

  if (haystack.includes("validation")) return "validation";
  if (haystack.includes("workflow")) return "workflow";
  if (haystack.includes("evidence")) return "evidence";
  if (haystack.includes("sop")) return "sop";
  if (haystack.includes("decision") || haystack.includes("recommendation") || haystack.includes("advisory")) return "decision";
  if (haystack.includes("operator") || haystack.includes("note")) return "operator";
  if (haystack.includes("block")) return "blocked";
  return "mission";
}

function countBySource(events) {
  return events.reduce(
    (counts, event) => {
      const key = eventSourceKey(event);
      counts[key] = (counts[key] || 0) + 1;
      return counts;
    },
    {
      mission: 0,
      validation: 0,
      workflow: 0,
      evidence: 0,
      sop: 0,
      decision: 0,
      operator: 0,
      blocked: 0,
    }
  );
}

const summaryGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  gap: 12,
};

const summaryTileStyle = {
  border: "1px solid rgba(96, 165, 250, 0.22)",
  borderRadius: 14,
  padding: "12px 14px",
  background: "rgba(15, 23, 42, 0.34)",
  display: "grid",
  gap: 4,
  minWidth: 0,
};

const summaryLabelStyle = {
  color: "#a8b3cf",
  fontSize: 11,
  fontWeight: 900,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
};

const summaryValueStyle = {
  color: "#60a5fa",
  fontSize: 22,
  lineHeight: 1.1,
  fontWeight: 500,
};

const noteStyle = {
  color: "#93c5fd",
  fontSize: 12.5,
  lineHeight: 1.5,
  margin: 0,
};

export default function HistoryExecutionTab({ events = [] }) {
  const rows = useMemo(
    () => (Array.isArray(events) ? events.filter(Boolean) : []),
    [events]
  );
  const counts = useMemo(() => countBySource(rows), [rows]);

  const operationalCount = counts.mission + counts.operator + counts.blocked;
  const validationEvidenceCount = counts.validation + counts.evidence;
  const workflowSopCount = counts.workflow + counts.sop;
  const decisionCount = counts.decision;

  return (
    <ExecutionSection
      title="Operational History"
      description="Unified task-scoped audit trail for Mission Execution, validation, evidence, workflow, SOP, Decision Support, and operator activity."
      badge={`${rows.length} event(s)`}
      badgeTone="default"
    >
      <div style={summaryGridStyle}>
        <div style={summaryTileStyle}>
          <span style={summaryLabelStyle}>Operational</span>
          <strong style={summaryValueStyle}>{operationalCount}</strong>
        </div>
        <div style={summaryTileStyle}>
          <span style={summaryLabelStyle}>Validation / Evidence</span>
          <strong style={summaryValueStyle}>{validationEvidenceCount}</strong>
        </div>
        <div style={summaryTileStyle}>
          <span style={summaryLabelStyle}>Workflow / SOP</span>
          <strong style={summaryValueStyle}>{workflowSopCount}</strong>
        </div>
        <div style={summaryTileStyle}>
          <span style={summaryLabelStyle}>Decision</span>
          <strong style={summaryValueStyle}>{decisionCount}</strong>
        </div>
      </div>

      <p style={noteStyle}>
        History uses the modal body for scrolling. There is no nested fixed-height timeline, which avoids the frozen-scroll regression.
      </p>

      <ExecutionTimeline events={rows} newestFirst />
    </ExecutionSection>
  );
}
